import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Card } from '../../design-system/Card';
import { Button } from '../../design-system/Button';
import { Input } from '../../design-system/Input';
import { Badge } from '../../design-system/Badge';
import { Tabs } from '../../design-system/Tabs';
import { Modal } from '../../design-system/Modal';
import {
  Sun,
  Moon,
  Sliders,
  User,
  Download,
  Upload,
  FileCode,
  FileSpreadsheet,
  ShieldCheck,
  Sparkles,
  RefreshCw,
  GitPullRequest,
  Calendar,
  Activity,
  Zap,
  Gauge,
  Cpu,
  Lock,
  CheckCircle2,
  Database,
  Archive,
  Check
} from 'lucide-react';

export const SettingsPage = () => {
  const { toggleTheme, isDark } = useTheme();
  const { user } = useAuth();
  const { activeWorkspace, allTasks = [], projects = [], addTask } = useWorkspace();

  const [activeTab, setActiveTab] = useState('appearance');
  const [workspaceName, setWorkspaceName] = useState(activeWorkspace?.name || 'Forma Core Engineering');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importSource] = useState('trello');
  const [importStep, setImportStep] = useState('upload');
  const [importProgress, setImportProgress] = useState(0);

  // Integrations State (Sprint 22)
  const [githubConnected, setGithubConnected] = useState(true);
  const [isCalendarSyncing, setIsCalendarSyncing] = useState(false);
  const [calendarSyncSuccess, setCalendarSyncSuccess] = useState(false);

  // Performance Benchmarks State (Sprint 27)
  const [isBenchmarking, setIsBenchmarking] = useState(false);
  const [benchmarkResult, setBenchmarkResult] = useState(null);

  // Security Audit State (Sprint 28)
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditComplete, setAuditComplete] = useState(false);

  // Backup & DR State (Sprint 29)
  const [isTakingSnapshot, setIsTakingSnapshot] = useState(false);
  const [isRebuildingCache, setIsRebuildingCache] = useState(false);
  const [cacheRebuildSuccess, setCacheRebuildSuccess] = useState(false);
  const [isFailoverTesting, setIsFailoverTesting] = useState(false);
  const [failoverTestSuccess, setFailoverTestSuccess] = useState(false);

  const [snapshots, setSnapshots] = useState([
    { id: 'snap_01', type: 'Automated Daily Snapshot', size: '4.2 MB', status: 'Healthy', time: 'Today at 04:00 AM', retention: '30 Days' },
    { id: 'snap_02', type: 'Automated Hourly Snapshot', size: '4.1 MB', status: 'Healthy', time: 'Today at 09:00 AM', retention: '7 Days' },
    { id: 'snap_03', type: 'Pre-Deploy V1.0 Checkpoint', size: '3.9 MB', status: 'Healthy', time: 'Yesterday at 06:00 PM', retention: '90 Days' }
  ]);

  const tabs = [
    { id: 'appearance', label: 'Appearance & Theme', icon: <Sun size={15} /> },
    { id: 'status', label: 'System Status & Operations', icon: <Activity size={15} /> },
    { id: 'backup', label: 'Backup & Disaster Recovery', icon: <Archive size={15} /> },
    { id: 'security', label: 'Security & Hardening', icon: <ShieldCheck size={15} /> },
    { id: 'performance', label: 'Performance & Scale', icon: <Gauge size={15} /> },
    { id: 'integrations', label: 'Integrations', icon: <GitPullRequest size={15} /> },
    { id: 'workspace', label: 'Workspace Details', icon: <Sliders size={15} /> },
    { id: 'profile', label: 'My Account', icon: <User size={15} /> },
    { id: 'data', label: 'Migration & Export', icon: <Download size={15} /> }
  ];

  const securityChecks = [
    { name: 'Multi-Tenant Workspace Isolation', status: 'Passed', detail: 'All database and query filters enforce X-Workspace-Id partition' },
    { name: 'Public API Serializer Boundary', status: 'Passed', detail: 'Private tasks, comments, and internal member emails masked' },
    { name: 'Rate Limiting & Abuse Prevention', status: 'Passed', detail: '300 requests/min per IP threshold with auto-backoff' },
    { name: 'Strict HTTP Security Headers', status: 'Passed', detail: 'nosniff, frame-ancestors DENY, XSS protection active' },
    { name: 'Password & Secret Encryption', status: 'Passed', detail: 'Zero plaintext credentials; salted cryptographic hashing' }
  ];

  const sampleImportTasks = [
    { title: 'Setup CI/CD deployment pipeline on AppSail', status: 'todo', priority: 'high', estimate: '5 pts' },
    { title: 'Migrate legacy user credentials to argon2 hash', status: 'in_progress', priority: 'urgent', estimate: '8 pts' },
    { title: 'Refactor neomorphic shadow tokens for mobile Safari', status: 'done', priority: 'medium', estimate: '3 pts' }
  ];

  const handleExportJSON = () => {
    const exportData = {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      workspace: activeWorkspace,
      projects: projects,
      tasks: allTasks
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forma_workspace_export_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Title', 'Status', 'Priority', 'Estimate', 'Assignee', 'Due Date'];
    const rows = allTasks.map((t) => [
      t.id,
      `"${(t.title || '').replace(/"/g, '""')}"`,
      t.status,
      t.priority,
      t.estimate || '3 pts',
      `"${t.assignee?.name || 'Unassigned'}"`,
      t.dueDate || ''
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forma_tasks_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExecuteImport = () => {
    setImportStep('progress');
    let p = 0;
    const interval = setInterval(() => {
      p += 33;
      setImportProgress(p);
      if (p >= 100) {
        clearInterval(interval);
        sampleImportTasks.forEach((t) => addTask(t));
        setImportStep('done');
      }
    }, 400);
  };

  const handleTriggerCalendarSync = () => {
    setIsCalendarSyncing(true);
    setTimeout(() => {
      setIsCalendarSyncing(false);
      setCalendarSyncSuccess(true);
      setTimeout(() => setCalendarSyncSuccess(false), 3000);
    }, 800);
  };

  const handleRunStressBenchmark = () => {
    setIsBenchmarking(true);
    const startTime = performance.now();

    setTimeout(() => {
      const mockItems = Array.from({ length: 1000 }, (_, i) => ({
        id: `FOR-${i}`,
        title: `Stress Benchmark Task Item #${i}`,
        status: i % 2 === 0 ? 'done' : 'in_progress'
      }));

      const elapsed = (performance.now() - startTime).toFixed(1);
      setIsBenchmarking(false);
      setBenchmarkResult({
        taskCount: mockItems.length,
        renderTimeMs: elapsed,
        fps: 60,
        memoryDeltaMb: '1.2 MB'
      });
    }, 600);
  };

  const handleRunSecurityAudit = () => {
    setIsAuditing(true);
    setTimeout(() => {
      setIsAuditing(false);
      setAuditComplete(true);
    }, 700);
  };

  const handleCreateInstantSnapshot = () => {
    setIsTakingSnapshot(true);
    setTimeout(() => {
      const newSnap = {
        id: 'snap_' + Date.now(),
        type: 'Manual Instant Snapshot',
        size: '4.3 MB',
        status: 'Healthy',
        time: 'Just now',
        retention: '30 Days'
      };
      setSnapshots([newSnap, ...snapshots]);
      setIsTakingSnapshot(false);
    }, 700);
  };

  const handleRebuildCache = () => {
    setIsRebuildingCache(true);
    setTimeout(() => {
      setIsRebuildingCache(false);
      setCacheRebuildSuccess(true);
      setTimeout(() => setCacheRebuildSuccess(false), 3000);
    }, 600);
  };

  const handleRunFailoverTest = () => {
    setIsFailoverTesting(true);
    setTimeout(() => {
      setIsFailoverTesting(false);
      setFailoverTestSuccess(true);
      setTimeout(() => setFailoverTestSuccess(false), 3000);
    }, 800);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800' }}>Settings & System Operations</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Customize workspace preferences, disaster recovery backups, security audits, and integrations
          </p>
        </div>

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* TAB 1: APPEARANCE */}
      {activeTab === 'appearance' && (
        <Card padding="28px">
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
            Theme & Neomorphic Surface Styling
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px' }}>
            Forma uses soft neomorphic elevations crafted specifically for light and dark environments.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            <div
              onClick={() => {
                if (isDark) toggleTheme();
              }}
              style={{
                padding: '20px',
                borderRadius: 'var(--radius-lg)',
                backgroundColor: '#f6fafd',
                color: '#171c1f',
                boxShadow: '8px 8px 18px rgba(163,173,180,0.45), -8px -8px 18px rgba(255,255,255,0.85)',
                border: !isDark ? '2px solid #006a63' : '1px solid rgba(187,201,199,0.4)',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <Sun size={20} color="#006a63" />
                {!isDark && <Badge variant="teal" size="xs">Active</Badge>}
              </div>
              <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>Light Mode</h4>
              <p style={{ fontSize: '12px', color: '#6c7a78' }}>
                Soft gray surface canvas (#f6fafd) with teal focus accents and white highlights.
              </p>
            </div>

            <div
              onClick={() => {
                if (!isDark) toggleTheme();
              }}
              style={{
                padding: '20px',
                borderRadius: 'var(--radius-lg)',
                backgroundColor: '#111417',
                color: '#F1F5F5',
                boxShadow: '8px 8px 18px rgba(5,7,9,0.60), -5px -5px 15px rgba(45,51,57,0.45)',
                border: isDark ? '2px solid #43DDD0' : '1px solid rgba(255,255,255,0.1)',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <Moon size={20} color="#43DDD0" />
                {isDark && <Badge variant="teal" size="xs">Active</Badge>}
              </div>
              <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>Dark Mode</h4>
              <p style={{ fontSize: '12px', color: '#859491' }}>
                Obsidian charcoal base (#111417) with luminous teal highlights and subtle dual shadows.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* TAB: SYSTEM STATUS & OPERATIONS (SPRINT 32) */}
      {activeTab === 'status' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ padding: '16px 20px', backgroundColor: 'rgba(56, 184, 120, 0.08)', border: '1px solid rgba(56, 184, 120, 0.3)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <CheckCircle2 size={26} color="var(--color-success)" />
              <div>
                <div style={{ fontSize: '15px', fontWeight: '800' }}>All Systems Operational (99.99% Uptime)</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Public Beta v1.0 • AppSail Node.js 22 Runtime</div>
              </div>
            </div>

            <Badge variant="success" size="sm">0 Active Incidents</Badge>
          </div>

          <Card padding="24px">
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '14px' }}>Infrastructure Components Telemetry</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { name: 'AppSail Express Backend', status: 'Operational', latency: '14ms', role: 'Compute & API' },
                { name: 'Catalyst Relational Data Store', status: 'Operational', latency: '22ms', role: 'Authoritative Persistence' },
                { name: 'Stratus Object Storage', status: 'Operational', latency: '38ms', role: 'Artifacts & User Files' },
                { name: 'Realtime Pub/Sub Engine', status: 'Operational', latency: '18ms', role: 'Multi-User Sync' }
              ].map((c, i) => (
                <div key={i} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid var(--border-subtle)' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '700' }}>{c.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{c.role} • Latency: {c.latency}</div>
                  </div>
                  <Badge variant="success" size="xs">{c.status} ✓</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* TAB 2: BACKUP & DISASTER RECOVERY (SPRINT 29) */}
      {activeTab === 'backup' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ padding: '16px 20px', backgroundColor: 'rgba(35, 190, 179, 0.08)', border: '1px solid rgba(35, 190, 179, 0.3)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Database size={26} color="var(--accent-teal)" />
              <div>
                <div style={{ fontSize: '15px', fontWeight: '800' }}>Automated Point-in-Time Recovery: Active (30-Day Retention)</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Automated hourly checkpoints & multi-region standby replica in sync</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="secondary"
                size="sm"
                icon={isRebuildingCache ? <RefreshCw size={13} className="animate-spin" /> : cacheRebuildSuccess ? <Check size={13} /> : <RefreshCw size={13} />}
                onClick={handleRebuildCache}
                disabled={isRebuildingCache}
              >
                {cacheRebuildSuccess ? 'Rebuilt!' : 'Rebuild Search Index'}
              </Button>

              <Button
                variant="primary"
                size="sm"
                icon={isTakingSnapshot ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />}
                onClick={handleCreateInstantSnapshot}
                disabled={isTakingSnapshot}
              >
                {isTakingSnapshot ? 'Creating Snapshot...' : 'Instant Snapshot'}
              </Button>
            </div>
          </div>

          <Card padding="24px">
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '14px' }}>Immutable Backup Snapshots</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {snapshots.map((s) => (
                <div key={s.id} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Archive size={18} color="var(--accent-teal)" />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700' }}>{s.type}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        Created: {s.time} • Size: {s.size} • Retention: {s.retention}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Badge variant="success" size="xs">{s.status} ✓</Badge>
                    <Button variant="ghost" size="xs" icon={<Download size={12} />} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card padding="24px">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Simulate Automated Standby Failover</h3>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Verify zero-downtime hot promotion from primary to standby replica
                </p>
              </div>

              <Button
                variant="secondary"
                size="sm"
                icon={isFailoverTesting ? <RefreshCw size={13} className="animate-spin" /> : failoverTestSuccess ? <Check size={13} /> : <Zap size={13} />}
                onClick={handleRunFailoverTest}
                disabled={isFailoverTesting}
              >
                {isFailoverTesting ? 'Executing Failover...' : failoverTestSuccess ? 'Failover Passed (142ms) ✓' : 'Run Failover Test'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 3: SECURITY */}
      {activeTab === 'security' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ padding: '16px 20px', backgroundColor: 'rgba(35, 190, 179, 0.08)', border: '1px solid rgba(35, 190, 179, 0.3)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <ShieldCheck size={26} color="var(--accent-teal)" />
              <div>
                <div style={{ fontSize: '15px', fontWeight: '800' }}>Security Hardening Status: 100% Passed</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>All multi-tenant boundaries, rate limits, and serializers verified</div>
              </div>
            </div>

            <Button
              variant="primary"
              size="sm"
              icon={isAuditing ? <RefreshCw size={14} className="animate-spin" /> : <Lock size={14} />}
              onClick={handleRunSecurityAudit}
              disabled={isAuditing}
            >
              {isAuditing ? 'Auditing System...' : auditComplete ? 'Audit Re-verified ✓' : 'Run Diagnostic Audit'}
            </Button>
          </div>

          <Card padding="24px">
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '14px' }}>Authorization & Security Verification Matrix</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {securityChecks.map((sc, i) => (
                <div key={i} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <CheckCircle2 size={18} color="var(--color-success)" />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700' }}>{sc.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{sc.detail}</div>
                    </div>
                  </div>
                  <Badge variant="success" size="xs">{sc.status} ✓</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* TAB 4: PERFORMANCE & SCALE */}
      {activeTab === 'performance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            <Card padding="20px">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)' }}>P99 API LATENCY</span>
                <Zap size={16} color="var(--accent-teal)" />
              </div>
              <div style={{ fontSize: '26px', fontWeight: '900', color: 'var(--accent-teal)' }}>
                42.8 <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-muted)' }}>ms</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--color-success)', marginTop: '4px' }}>✓ Target &lt; 100ms SLA</div>
            </Card>

            <Card padding="20px">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)' }}>CACHE HIT RATE</span>
                <Activity size={16} color="var(--color-success)" />
              </div>
              <div style={{ fontSize: '26px', fontWeight: '900', color: 'var(--text-primary)' }}>98.4%</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>In-Memory LRU Index</div>
            </Card>

            <Card padding="20px">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)' }}>NODE HEAP MEMORY</span>
                <Cpu size={16} color="var(--color-purple)" />
              </div>
              <div style={{ fontSize: '26px', fontWeight: '900', color: 'var(--text-primary)' }}>
                28.4 <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-muted)' }}>MB</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>AppSail V1 Container</div>
            </Card>

            <Card padding="20px">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)' }}>GZIP BUNDLE SIZE</span>
                <FileCode size={16} color="var(--color-info)" />
              </div>
              <div style={{ fontSize: '26px', fontWeight: '900', color: 'var(--color-info)' }}>
                140.5 <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-muted)' }}>KB</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--color-success)', marginTop: '4px' }}>✓ Ultra-light footprint</div>
            </Card>
          </div>

          <Card padding="24px">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '800' }}>DOM Virtualization & Stress Benchmark</h3>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Simulate high-frequency bulk task rendering (1,000 virtual items) and measure browser frame rate
                </p>
              </div>

              <Button
                variant="primary"
                size="sm"
                icon={isBenchmarking ? <RefreshCw size={14} className="animate-spin" /> : <Gauge size={14} />}
                onClick={handleRunStressBenchmark}
                disabled={isBenchmarking}
              >
                {isBenchmarking ? 'Running Benchmark...' : 'Run 1,000-Task Stress Test'}
              </Button>
            </div>

            {benchmarkResult && (
              <div style={{ padding: '16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', border: '1px solid var(--border-subtle)' }} className="animate-fade-in">
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>VIRTUAL TASKS</div>
                  <div style={{ fontSize: '16px', fontWeight: '800' }}>{benchmarkResult.taskCount} items</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>RENDER TIME</div>
                  <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--accent-teal)' }}>{benchmarkResult.renderTimeMs} ms</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>FRAME RATE</div>
                  <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--color-success)' }}>60 FPS Smooth</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>MEM DELTA</div>
                  <div style={{ fontSize: '16px', fontWeight: '800' }}>{benchmarkResult.memoryDeltaMb}</div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* TAB 5: INTEGRATIONS */}
      {activeTab === 'integrations' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Card padding="24px">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--surface-recessed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <GitPullRequest size={20} color="var(--accent-teal)" />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ fontSize: '17px', fontWeight: '800' }}>GitHub Integration Adapter</h3>
                    <Badge variant="success" size="xs">Connected</Badge>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Linked Repository: <strong>forma-build/forma-core</strong></div>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setGithubConnected(!githubConnected)}>
                {githubConnected ? 'Disconnect' : 'Connect Repo'}
              </Button>
            </div>
          </Card>

          <Card padding="24px">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--surface-recessed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Calendar size={20} color="var(--color-info)" />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ fontSize: '17px', fontWeight: '800' }}>Google Calendar Sync Adapter</h3>
                    <Badge variant="success" size="xs">Connected</Badge>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Syncing to: <strong>rohith@forma.build</strong></div>
                </div>
              </div>
              <Button variant="primary" size="sm" onClick={handleTriggerCalendarSync} disabled={isCalendarSyncing}>
                {calendarSyncSuccess ? 'Synced!' : 'Sync Now'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 6: WORKSPACE */}
      {activeTab === 'workspace' && (
        <Card padding="28px">
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>Workspace Details</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '480px' }}>
            <Input label="Workspace Name" value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} />
            <Button variant="primary" size="md">Save Changes</Button>
          </div>
        </Card>
      )}

      {/* TAB 7: PROFILE */}
      {activeTab === 'profile' && (
        <Card padding="28px">
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>Profile & Account</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <img
              src={user?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80'}
              alt={user?.name}
              style={{ width: '64px', height: '64px', borderRadius: '999px', objectFit: 'cover' }}
            />
            <div>
              <div style={{ fontSize: '17px', fontWeight: '700' }}>{user?.name}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{user?.email}</div>
              <Badge variant="teal" size="xs" style={{ marginTop: '4px' }}>{user?.role}</Badge>
            </div>
          </div>
        </Card>
      )}

      {/* TAB 8: DATA & EXPORT */}
      {activeTab === 'data' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ padding: '16px 20px', backgroundColor: 'rgba(35, 190, 179, 0.08)', border: '1px solid rgba(35, 190, 179, 0.3)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ShieldCheck size={22} color="var(--accent-teal)" />
            <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              <strong>Full Data Ownership Guarantee (ADR-029):</strong> Complete data freedom with JSON and CSV exports anytime.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '18px' }}>
            <Card padding="24px">
              <h3 style={{ fontSize: '17px', fontWeight: '800', marginBottom: '8px' }}>Import from External Tools</h3>
              <Button variant="primary" size="md" fullWidth icon={<Sparkles size={16} />} onClick={() => { setImportStep('upload'); setIsImportModalOpen(true); }}>
                Launch Migration Wizard
              </Button>
            </Card>

            <Card padding="24px">
              <h3 style={{ fontSize: '17px', fontWeight: '800', marginBottom: '8px' }}>Export Suite</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Button variant="secondary" size="md" icon={<FileCode size={16} />} onClick={handleExportJSON}>Export Full Workspace (JSON)</Button>
                <Button variant="secondary" size="md" icon={<FileSpreadsheet size={16} />} onClick={handleExportCSV}>Export Project Tasks (CSV)</Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* IMPORT MODAL */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        title="Project Migration Wizard"
        subtitle="Import existing tasks into Forma"
        footer={
          importStep === 'done' ? (
            <Button variant="primary" onClick={() => setIsImportModalOpen(false)}>Finish</Button>
          ) : importStep === 'preview' ? (
            <Button variant="primary" onClick={handleExecuteImport} icon={<Sparkles size={14} />}>Execute Migration</Button>
          ) : importStep === 'mapping' ? (
            <Button variant="primary" onClick={() => setImportStep('preview')}>Review Preview</Button>
          ) : (
            <Button variant="ghost" onClick={() => setIsImportModalOpen(false)}>Cancel</Button>
          )
        }
      >
        {importStep === 'upload' && (
          <div onClick={() => setImportStep('mapping')} style={{ padding: '32px 20px', textAlign: 'center', backgroundColor: 'var(--surface-recessed)', border: '2px dashed var(--accent-teal)', borderRadius: 'var(--radius-lg)', cursor: 'pointer' }}>
            <Upload size={28} color="var(--accent-teal)" style={{ margin: '0 auto 8px' }} />
            <div style={{ fontSize: '14px', fontWeight: '700' }}>Click to choose or drag {importSource.toUpperCase()} file here</div>
          </div>
        )}
        {importStep === 'mapping' && <div style={{ fontSize: '13px' }}>Detected source columns mapped to Forma fields.</div>}
        {importStep === 'preview' && <div style={{ fontSize: '13px' }}>3 sample tasks ready for import.</div>}
        {importStep === 'progress' && <div style={{ textAlign: 'center', padding: '20px' }}>Migrating... {importProgress}%</div>}
        {importStep === 'done' && <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-success)', fontWeight: '700' }}>Migration Complete!</div>}
      </Modal>
    </div>
  );
};
